import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { api, API_BASE, apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Palette,
  Languages,
  UserCog,
  Bell,
  SlidersHorizontal,
  ShieldCheck,
  MonitorSmartphone,
  Info,
  Sun,
  Moon,
  Laptop2,
  Loader2,
  KeyRound,
  Upload,
  Trash2,
  Check,
  CircleAlert,
} from "lucide-react";
import { format } from "date-fns";

const TABS = [
  { key: "appearance", labelKey: "settings.nav.appearance", icon: Palette },
  { key: "language", labelKey: "settings.nav.language", icon: Languages },
  { key: "profile", labelKey: "settings.nav.profile", icon: UserCog },
  { key: "notifications", labelKey: "settings.nav.notifications", icon: Bell },
  { key: "preferences", labelKey: "settings.nav.preferences", icon: SlidersHorizontal },
  { key: "security", labelKey: "settings.nav.security", icon: ShieldCheck },
  { key: "sessions", labelKey: "settings.nav.sessions", icon: MonitorSmartphone },
  { key: "system", labelKey: "settings.nav.system", icon: Info },
];

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();
  const { t, lang, setLang } = useLang();
  const { theme, setTheme } = useTheme();
  const [active, setActive] = useState("appearance");
  const [data, setData] = useState(null);

  async function load() {
    try {
      const { data: d } = await api.get("/settings");
      setData(d);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function savePreferences(patch) {
    try {
      const { data: d } = await api.patch("/settings/preferences", patch);
      setData((prev) => ({ ...prev, preferences: d.preferences }));
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  if (!data)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-accent font-semibold mb-2">
          {t("nav.settings")}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
          {t("settings.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
          {t("settings.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Left nav */}
        <aside className="bg-[hsl(var(--surface))] border border-border rounded-lg p-2 h-fit lg:sticky lg:top-24">
          {TABS.map(({ key, labelKey, icon: Icon }) => (
            <button
              key={key}
              data-testid={`settings-tab-${key}`}
              onClick={() => setActive(key)}
              className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                active === key
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/80 hover:bg-[hsl(var(--panel-alt))]"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              {t(labelKey)}
            </button>
          ))}
        </aside>

        {/* Right content */}
        <div className="space-y-6">
          {active === "appearance" && (
            <AppearanceSection
              theme={theme}
              setTheme={setTheme}
              onSave={(v) => savePreferences({ theme: v })}
            />
          )}
          {active === "language" && (
            <LanguageSection
              lang={lang}
              setLang={setLang}
              onSave={(v) => savePreferences({ language: v })}
            />
          )}
          {active === "profile" && (
            <ProfileSection data={data} onSaved={load} refreshUser={refreshUser} />
          )}
          {active === "notifications" && (
            <NotificationsSection
              prefs={data.preferences.notifications}
              onSave={(n) => savePreferences({ notifications: n })}
            />
          )}
          {active === "preferences" && (
            <AppPrefsSection
              prefs={data.preferences.app}
              onSave={(a) => savePreferences({ app: a })}
            />
          )}
          {active === "security" && (
            <SecuritySection
              user={user}
              enabled={data.two_factor_enabled}
              onChanged={load}
            />
          )}
          {active === "sessions" && <SessionsSection />}
          {active === "system" && <SystemSection user={user} />}
        </div>
      </div>
    </div>
  );
}

/* ─── Sections ────────────────────────────────────────────────────────── */

function SectionCard({ title, description, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-[hsl(var(--surface))] border border-border rounded-lg p-6"
    >
      <h2 className="text-lg font-bold text-foreground tracking-tight">{title}</h2>
      {description && (
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      )}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function AppearanceSection({ theme, setTheme, onSave }) {
  const { t } = useLang();
  const opts = [
    { v: "light", label: t("settings.theme.light"), icon: Sun },
    { v: "dark", label: t("settings.theme.dark"), icon: Moon },
    { v: "system", label: t("settings.theme.system"), icon: Laptop2 },
  ];
  return (
    <SectionCard
      testId="section-appearance"
      title={t("settings.appearance.title")}
      description={t("settings.appearance.desc")}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {opts.map(({ v, label, icon: Icon }) => (
          <button
            key={v}
            data-testid={`theme-opt-${v}`}
            onClick={() => {
              setTheme(v);
              onSave(v);
            }}
            className={`relative flex flex-col items-start gap-3 p-4 rounded-md border-2 transition-all text-left ${
              theme === v
                ? "border-accent bg-accent/5"
                : "border-border hover:border-accent/40"
            }`}
          >
            <div className="w-10 h-10 rounded-md bg-[hsl(var(--panel-alt))] flex items-center justify-center">
              <Icon className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">{label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {v === "system" ? "Auto" : v === "dark" ? "Low light" : "Bright"}
              </div>
            </div>
            {theme === v && (
              <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                <Check className="w-3.5 h-3.5" />
              </div>
            )}
          </button>
        ))}
      </div>
    </SectionCard>
  );
}

function LanguageSection({ lang, setLang, onSave }) {
  const { t } = useLang();
  const opts = [
    { v: "en", label: "English", sub: "Latin" },
    { v: "hi", label: "हिन्दी", sub: "Devanagari" },
  ];
  return (
    <SectionCard
      testId="section-language"
      title={t("settings.language.title")}
      description={t("settings.language.desc")}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {opts.map(({ v, label, sub }) => (
          <button
            key={v}
            data-testid={`lang-opt-${v}`}
            onClick={() => {
              setLang(v);
              onSave(v);
            }}
            className={`relative flex items-center gap-4 p-4 rounded-md border-2 transition-all text-left ${
              lang === v
                ? "border-accent bg-accent/5"
                : "border-border hover:border-accent/40"
            }`}
          >
            <div className="w-12 h-12 rounded-md bg-[hsl(var(--panel-alt))] flex items-center justify-center font-bold text-lg">
              {v === "hi" ? "अ" : "Aa"}
            </div>
            <div>
              <div
                className={`text-base font-bold text-foreground ${
                  v === "hi" ? "font-devanagari" : ""
                }`}
              >
                {label}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
            </div>
            {lang === v && (
              <div className="ml-auto w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                <Check className="w-3.5 h-3.5" />
              </div>
            )}
          </button>
        ))}
      </div>
    </SectionCard>
  );
}

function ProfileSection({ data, onSaved, refreshUser }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: data.profile.name || "",
    email: data.profile.email || "",
    mobile: data.profile.mobile || "",
    avatar_id: data.profile.avatar_id || null,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const initial = useMemo(
    () => JSON.stringify({
      name: data.profile.name || "",
      email: data.profile.email || "",
      mobile: data.profile.mobile || "",
      avatar_id: data.profile.avatar_id || null,
    }),
    [data.profile]
  );
  const dirty = JSON.stringify(form) !== initial;

  async function onPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(f.type)) {
      toast.error("Please upload a PNG, JPG, WEBP or GIF");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data: uploaded } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, avatar_id: uploaded.id }));
      toast.success("Uploaded — click Save to apply");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function save() {
    setSaving(true);
    try {
      await api.patch("/settings/profile", form);
      await refreshUser?.();
      toast.success(t("settings.saved"));
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const avatarUrl = form.avatar_id ? `${API_BASE}/files/${form.avatar_id}` : null;
  const initials = (form.name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <SectionCard
      testId="section-profile"
      title={t("settings.profile.title")}
      description={t("settings.profile.desc")}
    >
      <div className="flex items-start gap-6 pb-6 border-b border-border">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="w-20 h-20 rounded-full object-cover ring-2 ring-border"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-[hsl(var(--panel-alt))] flex items-center justify-center text-xl font-bold text-foreground">
            {initials}
          </div>
        )}
        <div className="flex-1">
          <div className="text-sm font-semibold text-foreground">
            {t("settings.profile.avatar")}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {t("settings.profile.avatar_hint")}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              id="avatar-input"
              data-testid="avatar-input"
              type="file"
              accept="image/*"
              onChange={onPick}
              className="hidden"
            />
            <label
              htmlFor="avatar-input"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border bg-[hsl(var(--surface))] text-sm font-semibold text-foreground hover:bg-[hsl(var(--panel-alt))] cursor-pointer"
            >
              {uploading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5" />
              )}
              {t("settings.profile.upload")}
            </label>
            {form.avatar_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setForm((f) => ({ ...f, avatar_id: null }))}
                data-testid="avatar-remove"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                {t("settings.profile.remove")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
        <div>
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.profile.name")}
          </Label>
          <Input
            data-testid="profile-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="mt-1.5 h-11"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.profile.mobile")}
          </Label>
          <Input
            data-testid="profile-mobile"
            value={form.mobile}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                mobile: e.target.value.replace(/[^0-9+]/g, "").slice(0, 13),
              }))
            }
            className="mt-1.5 h-11 font-mono-cms"
            placeholder="10-digit mobile"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.profile.email")}
          </Label>
          <Input
            data-testid="profile-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="mt-1.5 h-11"
          />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={() => setForm(JSON.parse(initial))}
          disabled={!dirty || saving}
          data-testid="profile-cancel"
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="profile-save"
          className="bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </div>
    </SectionCard>
  );
}

function ToggleRow({ testId, label, hint, value, onChange }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-border last:border-0">
      <div className="pr-6">
        <div className="text-sm font-semibold text-foreground">{label}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      <Switch data-testid={testId} checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}

function NotificationsSection({ prefs, onSave }) {
  const { t } = useLang();
  const [state, setState] = useState(prefs);
  useEffect(() => setState(prefs), [prefs]);

  function toggle(k, v) {
    const next = { ...state, [k]: v };
    setState(next);
    onSave(next);
  }

  return (
    <SectionCard
      testId="section-notifications"
      title={t("settings.notify.title")}
      description={t("settings.notify.desc")}
    >
      <ToggleRow
        testId="notify-email"
        label={t("settings.notify.email")}
        hint={t("settings.notify.email_hint")}
        value={state.email}
        onChange={(v) => toggle("email", v)}
      />
      <ToggleRow
        testId="notify-sms"
        label={t("settings.notify.sms")}
        hint={t("settings.notify.sms_hint")}
        value={state.sms}
        onChange={(v) => toggle("sms", v)}
      />
      <ToggleRow
        testId="notify-inapp"
        label={t("settings.notify.in_app")}
        hint={t("settings.notify.in_app_hint")}
        value={state.in_app}
        onChange={(v) => toggle("in_app", v)}
      />
    </SectionCard>
  );
}

function AppPrefsSection({ prefs, onSave }) {
  const { t } = useLang();
  const [state, setState] = useState(prefs);
  useEffect(() => setState(prefs), [prefs]);
  const dirty = JSON.stringify(state) !== JSON.stringify(prefs);

  return (
    <SectionCard
      testId="section-preferences"
      title={t("settings.prefs.title")}
      description={t("settings.prefs.desc")}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.prefs.dashboard_view")}
          </Label>
          <Select
            value={state.default_dashboard_view}
            onValueChange={(v) =>
              setState((s) => ({ ...s, default_dashboard_view: v }))
            }
          >
            <SelectTrigger data-testid="prefs-view" className="mt-1.5 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="grid">Grid</SelectItem>
              <SelectItem value="list">List</SelectItem>
              <SelectItem value="compact">Compact</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.prefs.landing")}
          </Label>
          <Select
            value={state.default_landing}
            onValueChange={(v) => setState((s) => ({ ...s, default_landing: v }))}
          >
            <SelectTrigger data-testid="prefs-landing" className="mt-1.5 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="/">Dashboard</SelectItem>
              <SelectItem value="/complaints">All Complaints</SelectItem>
              <SelectItem value="/departments">Departments</SelectItem>
              <SelectItem value="/search">Track by Mobile</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.prefs.date")}
          </Label>
          <Select
            value={state.date_format}
            onValueChange={(v) => setState((s) => ({ ...s, date_format: v }))}
          >
            <SelectTrigger data-testid="prefs-date" className="mt-1.5 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DD MMM YYYY">04 Jul 2026</SelectItem>
              <SelectItem value="DD/MM/YYYY">04/07/2026</SelectItem>
              <SelectItem value="YYYY-MM-DD">2026-07-04</SelectItem>
              <SelectItem value="MM/DD/YYYY">07/04/2026</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.prefs.time")}
          </Label>
          <Select
            value={state.time_format}
            onValueChange={(v) => setState((s) => ({ ...s, time_format: v }))}
          >
            <SelectTrigger data-testid="prefs-time" className="mt-1.5 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">24-hour (14:30)</SelectItem>
              <SelectItem value="12h">12-hour (02:30 PM)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm font-semibold text-foreground">
            {t("settings.prefs.tz")}
          </Label>
          <Select
            value={state.time_zone}
            onValueChange={(v) => setState((s) => ({ ...s, time_zone: v }))}
          >
            <SelectTrigger data-testid="prefs-tz" className="mt-1.5 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Asia/Kolkata">Asia/Kolkata (IST · UTC+5:30)</SelectItem>
              <SelectItem value="Asia/Dubai">Asia/Dubai (UTC+4)</SelectItem>
              <SelectItem value="Asia/Singapore">Asia/Singapore (UTC+8)</SelectItem>
              <SelectItem value="Europe/London">Europe/London</SelectItem>
              <SelectItem value="America/New_York">America/New_York</SelectItem>
              <SelectItem value="UTC">UTC</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-end gap-2 pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={() => setState(prefs)}
          disabled={!dirty}
          data-testid="prefs-cancel"
        >
          {t("common.cancel")}
        </Button>
        <Button
          onClick={() => onSave(state)}
          disabled={!dirty}
          data-testid="prefs-save"
          className="bg-accent hover:bg-accent/90 text-accent-foreground"
        >
          {t("common.save")}
        </Button>
      </div>
    </SectionCard>
  );
}

function SecuritySection({ user, enabled, onChanged }) {
  const { t } = useLang();
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  async function updatePw() {
    if (pw.next.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/settings/password", {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password updated");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSavingPw(false);
    }
  }

  const [tfa, setTfa] = useState({ step: "idle", qr: null, secret: "", code: "" });

  async function startTfa() {
    try {
      const { data } = await api.post("/settings/2fa/setup");
      setTfa({ step: "verify", qr: data.qr_png_base64, secret: data.secret, code: "" });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function verifyTfa() {
    try {
      await api.post("/settings/2fa/verify", { code: tfa.code });
      toast.success("2FA enabled");
      setTfa({ step: "idle", qr: null, secret: "", code: "" });
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function disableTfa() {
    if (!tfa.code) {
      toast.error("Enter your current 2FA code");
      return;
    }
    try {
      await api.post("/settings/2fa/disable", { code: tfa.code });
      toast.success("2FA disabled");
      setTfa({ step: "idle", qr: null, secret: "", code: "" });
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <>
      <SectionCard
        testId="section-security-password"
        title={t("settings.security.change_pw")}
        description={t("settings.security.desc")}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-sm font-semibold text-foreground">
              {t("settings.security.current_pw")}
            </Label>
            <Input
              type="password"
              data-testid="pw-current"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-foreground">
              {t("settings.security.new_pw")}
            </Label>
            <Input
              type="password"
              data-testid="pw-new"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              className="mt-1.5 h-11"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-foreground">
              {t("settings.security.confirm_pw")}
            </Label>
            <Input
              type="password"
              data-testid="pw-confirm"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              className="mt-1.5 h-11"
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button
            data-testid="pw-submit"
            onClick={updatePw}
            disabled={savingPw || !pw.current || !pw.next}
            className="bg-accent hover:bg-accent/90 text-accent-foreground"
          >
            <KeyRound className="w-4 h-4 mr-1.5" />
            {savingPw ? t("common.saving") : t("settings.security.update_pw")}
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        testId="section-security-2fa"
        title={t("settings.security.2fa")}
        description={t("settings.security.2fa_hint")}
      >
        <div className="flex items-center justify-between p-4 rounded-md bg-[hsl(var(--panel-alt))] border border-border">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-md flex items-center justify-center ${
                enabled ? "bg-green-500/10 text-green-600" : "bg-orange-500/10 text-accent"
              }`}
            >
              {enabled ? <ShieldCheck className="w-5 h-5" /> : <CircleAlert className="w-5 h-5" />}
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {enabled ? "Enabled" : "Not enabled"}
              </div>
              <div className="text-xs text-muted-foreground">
                {enabled
                  ? "Your account is protected with an authenticator code."
                  : "Add an authenticator to strengthen your login."}
              </div>
            </div>
          </div>
          {!enabled && tfa.step === "idle" && (
            <Button
              onClick={startTfa}
              data-testid="tfa-enable-start"
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {t("settings.security.2fa_enable")}
            </Button>
          )}
          {enabled && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="tfa-disable-open">
                  {t("settings.security.2fa_disable")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disable two-factor authentication?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Enter your current 6-digit code to confirm.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <Input
                  data-testid="tfa-disable-code"
                  placeholder="123456"
                  value={tfa.code}
                  onChange={(e) => setTfa({ ...tfa, code: e.target.value.replace(/[^0-9]/g, "").slice(0, 6) })}
                  className="h-11 text-center font-mono-cms text-lg tracking-widest"
                />
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={disableTfa}
                    data-testid="tfa-disable-confirm"
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    Disable
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {tfa.step === "verify" && (
          <div className="mt-5 border border-border rounded-md p-5">
            <div className="text-sm font-semibold text-foreground mb-2">
              {t("settings.security.2fa_step_scan")}
            </div>
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {tfa.qr && (
                <img
                  src={`data:image/png;base64,${tfa.qr}`}
                  alt="QR"
                  className="w-40 h-40 rounded-md border border-border bg-white p-2"
                />
              )}
              <div className="flex-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  Manual key
                </div>
                <code
                  data-testid="tfa-secret"
                  className="mt-1.5 block font-mono-cms text-sm break-all bg-[hsl(var(--panel-alt))] rounded px-3 py-2 border border-border"
                >
                  {tfa.secret}
                </code>
                <div className="mt-4 text-sm font-semibold text-foreground">
                  {t("settings.security.2fa_step_verify")}
                </div>
                <Input
                  data-testid="tfa-enable-code"
                  placeholder="123456"
                  value={tfa.code}
                  onChange={(e) =>
                    setTfa({
                      ...tfa,
                      code: e.target.value.replace(/[^0-9]/g, "").slice(0, 6),
                    })
                  }
                  className="mt-2 h-11 text-center font-mono-cms text-lg tracking-widest max-w-[200px]"
                />
                <div className="mt-4 flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setTfa({ step: "idle", qr: null, secret: "", code: "" })}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={verifyTfa}
                    data-testid="tfa-enable-verify"
                    disabled={tfa.code.length !== 6}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground"
                  >
                    {t("settings.security.2fa_verify")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </SectionCard>
    </>
  );
}

function SessionsSection() {
  const { t } = useLang();
  const [sessions, setSessions] = useState(null);

  async function load() {
    const { data } = await api.get("/settings/sessions");
    setSessions(data.items);
  }
  useEffect(() => {
    load();
  }, []);

  async function revoke(sid) {
    try {
      await api.delete(`/settings/sessions/${sid}`);
      toast.success("Session revoked");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }
  async function logoutOthers() {
    try {
      const { data } = await api.delete("/settings/sessions/others");
      toast.success(`${data.removed} session(s) removed`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }
  async function logoutAll() {
    try {
      await api.delete("/settings/sessions/all");
      toast.success("Logged out from all devices");
      window.location.href = "/login";
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  return (
    <SectionCard
      testId="section-sessions"
      title={t("settings.sessions.title")}
      description={t("settings.sessions.desc")}
    >
      <div className="space-y-2">
        {(sessions || []).map((s) => (
          <div
            key={s.session_id}
            data-testid={`session-${s.session_id}`}
            className="flex items-center justify-between p-3 rounded-md border border-border bg-[hsl(var(--panel-alt))]"
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                <MonitorSmartphone className="w-4 h-4" />
                <span className="truncate max-w-md" title={s.user_agent}>
                  {s.user_agent ? shortenUA(s.user_agent) : "Unknown device"}
                </span>
                {s.is_current && (
                  <span className="text-[10px] uppercase font-mono-cms font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded">
                    {t("settings.sessions.current")}
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 font-mono-cms">
                IP {s.ip} · last active {format(new Date(s.last_active), "dd MMM · HH:mm")}
              </div>
            </div>
            {!s.is_current && (
              <Button
                variant="outline"
                size="sm"
                data-testid={`revoke-${s.session_id}`}
                onClick={() => revoke(s.session_id)}
              >
                {t("settings.sessions.revoke")}
              </Button>
            )}
          </div>
        ))}
        {sessions && sessions.length === 0 && (
          <div className="text-sm text-muted-foreground py-4">No sessions found.</div>
        )}
        {!sessions && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-border flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          onClick={logoutOthers}
          data-testid="logout-others-btn"
          disabled={!sessions || sessions.filter((s) => !s.is_current).length === 0}
        >
          {t("settings.sessions.logout_others")}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              data-testid="logout-all-btn"
              className="text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
            >
              {t("settings.sessions.logout_all")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Log out from all devices?</AlertDialogTitle>
              <AlertDialogDescription>
                You will need to sign in again on every device, including this one.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={logoutAll}
                data-testid="logout-all-confirm"
                className="bg-destructive hover:bg-destructive/90"
              >
                Log out everywhere
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SectionCard>
  );
}

function shortenUA(ua) {
  if (/Chrome/.test(ua) && /Mobile/.test(ua)) return "Chrome on Mobile";
  if (/Chrome/.test(ua)) return "Chrome on Desktop";
  if (/Firefox/.test(ua)) return "Firefox";
  if (/Safari/.test(ua) && /Mobile/.test(ua)) return "Safari on iOS";
  if (/Safari/.test(ua)) return "Safari on Mac";
  if (/curl/i.test(ua)) return "curl (API)";
  return ua.slice(0, 60);
}

function SystemSection({ user }) {
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/settings/system").then((r) => setInfo(r.data));
  }, []);
  if (!info)
    return (
      <SectionCard
        testId="section-system"
        title={t("settings.system.title")}
        description={t("settings.system.desc")}
      >
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      </SectionCard>
    );
  const rows = [
    [t("settings.system.version"), info.app_version],
    [t("settings.system.role"), (user?.role || "").toUpperCase()],
    [t("settings.system.last_login"), info.last_login_at ? format(new Date(info.last_login_at), "dd MMM yyyy · HH:mm") : "—"],
    [t("settings.system.ip"), info.last_login_ip || "—"],
    [t("settings.system.device"), shortenUA(info.user_agent)],
    [t("settings.system.server_time"), info.server_time_utc ? format(new Date(info.server_time_utc), "dd MMM yyyy · HH:mm 'UTC'") : "—"],
  ];
  return (
    <SectionCard
      testId="section-system"
      title={t("settings.system.title")}
      description={t("settings.system.desc")}
    >
      <div className="divide-y divide-border">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between py-3">
            <div className="text-sm text-muted-foreground uppercase tracking-wider font-semibold">
              {k}
            </div>
            <div className="text-sm font-mono-cms font-semibold text-foreground text-right">
              {v}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
